/**
 * Toast helpers — thin wrappers around sonner that enforce voice rules.
 *
 * Polish queue, task 7 of 7. Why this exists:
 *   330+ toast.success/error calls were drifting in tone — some shouted
 *   ("Saved!"), some narrated ("Successfully created customer"), some
 *   were specific ("Workout shipped to library"). The specific ones
 *   feel best — confident, plain, no exclamation. This module bakes
 *   that voice into the API so it's easier to do the right thing
 *   than the wrong thing.
 *
 * Rules:
 *   - No exclamation marks. The toast itself is the celebration; the
 *     punctuation is shouting.
 *   - No "Successfully X" prefix. State the outcome plainly: "Workout
 *     saved", not "Successfully saved workout".
 *   - Failures should say what to DO when possible, not just what
 *     happened. The `details` slot below makes that easy.
 *
 * You can keep using `import { toast } from "sonner"` directly when
 * none of these helpers fit (e.g. toast.loading, toast.promise). This
 * file just makes the common case ergonomic + consistent.
 */

import { toast } from "sonner";

/**
 * Strips trailing "!" so callers can't sneak shouting back in. Logs a
 * dev-only warning if they tried — so we catch new violations during
 * authoring, not later when someone is grepping for tone.
 */
function dedupeBang(message: string): string {
  if (message.endsWith("!")) {
    if (import.meta.env.DEV) {
      console.warn(
        `[toast] dropped trailing "!" from "${message}". Toast itself is the celebration; let the punctuation be quiet.`,
      );
    }
    return message.replace(/!+$/, "");
  }
  return message;
}

/**
 * Success toast — confident, specific, no punctuation theatre.
 * Examples (good): "Workout saved", "Program shipped to Sarah",
 * "Earnings logged". Examples (bad → flagged): "Saved!", "Successfully
 * saved workout".
 */
export function notifySuccess(message: string, description?: string) {
  return toast.success(dedupeBang(message), description ? { description } : undefined);
}

/**
 * Error toast. Pair the message with optional `details` that tells
 * the user what to try next. Example: notifyError("Couldn't connect
 * to Stripe", "Check your API key in Settings → Integrations.")
 */
export function notifyError(message: string, details?: string) {
  return toast.error(dedupeBang(message), details ? { description: details } : undefined);
}

/** Neutral info — use sparingly; most info messages should be inline
 *  UI hints, not toasts. */
export function notifyInfo(message: string, description?: string) {
  return toast(dedupeBang(message), description ? { description } : undefined);
}

/** Warning — yellow tint. Use for "you can proceed but…". */
export function notifyWarning(message: string, description?: string) {
  return toast.warning(dedupeBang(message), description ? { description } : undefined);
}

/** Re-export sonner's toast for the edge cases (loading, promise,
 *  custom JSX) the helpers don't cover. */
export { toast };
